import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  BarChart3, 
  TrendingUp, 
  Users, 
  CreditCard, 
  MapPin,
  Calendar,
  Download,
  Loader2,
  DollarSign,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export const SuperadminStatistics = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchStatistics();
  }, []);

  const fetchStatistics = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/superadmin/statistics/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching statistics:', error);
      toast.error('Fehler beim Laden der Statistiken');
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    if (!stats) return;
    
    setExporting(true);
    
    try {
      // Prepare CSV data
      const csvData = [
        ['AllFlowX Statistik-Export', new Date().toLocaleDateString('de-DE')],
        [],
        ['Übersicht'],
        ['Metrik', 'Wert'],
        ['Gesamt Kunden', stats.overview.total_customers],
        ['Aktive Kunden', stats.overview.active_customers],
        ['Pending Kunden', stats.overview.pending_customers],
        ['Gesamt Standorte', stats.overview.total_locations],
        [],
        ['Abonnements'],
        ['Status', 'Anzahl'],
        ['Aktive Stripe-Abos', stats.subscriptions.stripe_available ? stats.subscriptions.active : 'n/v'],
        ['Trial (Stripe)', stats.subscriptions.stripe_available ? stats.subscriptions.trialing : 'n/v'],
        ['Gekündigt (Stripe)', stats.subscriptions.stripe_available ? stats.subscriptions.canceled : 'n/v'],
        ['Past Due (Stripe)', stats.subscriptions.stripe_available ? stats.subscriptions.past_due : 'n/v'],
        [],
        ['Umsatz'],
        ['Metrik', 'Betrag (€)'],
        ['Netto-MRR (Stripe, nach Dauerrabatt)', stats.revenue.mrr_available ? stats.revenue.net_mrr_eur : 'nicht verfügbar'],
        ['Eingegangene Zahlungen (gesamt, alle Zeit)', stats.revenue.collected_payments_total_eur],
        ['Ø eingegangen pro Kunde', stats.revenue.avg_revenue_per_customer],
        [],
        ['Wachstum'],
        ['Neue Kunden diesen Monat', stats.growth.new_customers_this_month],
        ['Ø Kundenlaufzeit (Tage)', stats.growth.avg_customer_lifetime_days],
      ];

      // Convert to CSV string
      const csvContent = csvData.map(row => row.join(',')).join('\n');
      
      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `allflowx-statistik-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('Export erfolgreich heruntergeladen');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Fehler beim Export');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  if (!stats) {
    return (
      <DashboardLayout>
        <div className="p-4 sm:p-6 lg:p-8">
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground">Keine Statistiken verfügbar</p>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-6">
        
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <BarChart3 className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold">Statistik-Dashboard</h1>
              <p className="text-sm text-muted-foreground">
                Übersicht über Kunden, Umsatz und Abonnements
              </p>
            </div>
          </div>
          <Button onClick={exportToCSV} disabled={exporting}>
            {exporting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Exportiere...
              </>
            ) : (
              <>
                <Download className="h-4 w-4 mr-2" />
                CSV Export
              </>
            )}
          </Button>
        </div>

        {/* Overview Cards */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Gesamt Kunden</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.overview.total_customers}</div>
              <p className="text-xs text-muted-foreground">
                {stats.overview.active_customers} aktiv, {stats.overview.pending_customers} pending
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Netto-MRR</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold" data-testid="stat-net-mrr">
                {stats.revenue.mrr_available ? `${Number(stats.revenue.net_mrr_eur).toFixed(2)}€` : 'n/v'}
              </div>
              <p className="text-xs text-muted-foreground">
                {stats.revenue.mrr_available
                  ? 'Stripe · netto · nach Dauerrabatt · exkl. Steuer'
                  : (stats.revenue.stripe_error || 'Stripe nicht verfügbar')}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Aktive Stripe-Abos</CardTitle>
              <CreditCard className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold" data-testid="stat-active-stripe-subs">
                {stats.subscriptions.stripe_available && stats.subscriptions.active !== null ? stats.subscriptions.active : 'n/v'}
              </div>
              <p className="text-xs text-muted-foreground">
                {stats.subscriptions.stripe_available
                  ? `${stats.subscriptions.trialing ?? 0} im Trial · Stripe status=active (MRR kann 0 sein)`
                  : 'Stripe nicht verfügbar'}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Standorte</CardTitle>
              <MapPin className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.overview.total_locations}</div>
              <p className="text-xs text-muted-foreground">
                Ø {(stats.overview.total_locations / stats.overview.total_customers).toFixed(1)} pro Kunde
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Revenue & Growth */}
        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Umsatz-Übersicht
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Eingegangene Zahlungen (gesamt):</span>
                  <span className="text-lg font-bold text-green-600">
                    {Number(stats.revenue.collected_payments_total_eur).toFixed(2)}€
                  </span>
                </div>
                <p className="text-xs text-muted-foreground -mt-1">Bezahlte Transaktionen, alle Zeit · kein MRR</p>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Ø eingegangen pro Kunde:</span>
                  <span className="text-sm font-medium">
                    {Number(stats.revenue.avg_revenue_per_customer).toFixed(2)}€
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Netto-MRR (vertraglich, nach Dauerrabatt):</span>
                  <span className="text-sm font-medium text-blue-600">
                    {stats.revenue.mrr_available ? `${Number(stats.revenue.net_mrr_eur).toFixed(2)}€/Monat` : 'n/v'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Statusbasis:</span>
                  <span className="text-xs font-medium text-muted-foreground">
                    {stats.revenue.mrr_status_basis
                      ? `${stats.revenue.mrr_status_basis.active_subscriptions ?? '–'} aktiv · ${stats.revenue.mrr_status_basis.trialing_subscriptions ?? '–'} Test · ${stats.revenue.mrr_status_basis.past_due_subscriptions ?? '–'} überfällig`
                      : '–'}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Wachstum
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Neue Kunden (Monat):</span>
                  <Badge variant="outline" className="text-green-600 border-green-600">
                    +{stats.growth.new_customers_this_month}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Ø Kundenlaufzeit:</span>
                  <span className="text-sm font-medium">
                    {stats.growth.avg_customer_lifetime_days} Tage
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Erfolgreiche Transaktionen:</span>
                  <span className="text-sm font-medium text-green-600">
                    {stats.transactions.successful} / {stats.transactions.total}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Subscription Status */}
        <Card>
          <CardHeader>
            <CardTitle>Abonnement-Status (Stripe)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div className="flex items-center gap-3 p-3 bg-green-50 dark:bg-green-950/20 rounded-lg">
                <CheckCircle className="h-8 w-8 text-green-600" />
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Aktiv</p>
                  <p className="text-2xl font-bold">{stats.subscriptions.stripe_available ? (stats.subscriptions.active ?? 0) : 'n/v'}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3 p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg">
                <Clock className="h-8 w-8 text-blue-600" />
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Trial</p>
                  <p className="text-2xl font-bold">{stats.subscriptions.stripe_available ? (stats.subscriptions.trialing ?? 0) : 'n/v'}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3 p-3 bg-yellow-50 dark:bg-yellow-950/20 rounded-lg">
                <AlertTriangle className="h-8 w-8 text-yellow-600" />
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Past Due</p>
                  <p className="text-2xl font-bold">{stats.subscriptions.stripe_available ? (stats.subscriptions.past_due ?? 0) : 'n/v'}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3 p-3 bg-red-50 dark:bg-red-950/20 rounded-lg">
                <XCircle className="h-8 w-8 text-red-600" />
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Gekündigt</p>
                  <p className="text-2xl font-bold">{stats.subscriptions.stripe_available ? (stats.subscriptions.canceled ?? 0) : 'n/v'}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Top Customers & Recent Signups */}
        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Top Kunden (nach Standorten)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {stats.top_customers.length > 0 ? (
                  stats.top_customers.map((customer, index) => (
                    <div key={customer.customer_id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Badge variant="outline">#{index + 1}</Badge>
                        <div>
                          <p className="text-sm font-medium">{customer.company_name}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm font-bold">{customer.location_count}</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">Keine Daten verfügbar</p>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Neueste Anmeldungen</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {stats.recent_signups.length > 0 ? (
                  stats.recent_signups.slice(0, 5).map((customer) => (
                    <div key={customer.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                      <div>
                        <p className="text-sm font-medium">{customer.company_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(customer.created_at).toLocaleDateString('de-DE')}
                        </p>
                      </div>
                      <Badge 
                        variant={customer.status === 'active' ? 'default' : 'outline'}
                      >
                        {customer.status}
                      </Badge>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">Keine Daten verfügbar</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};
